/*
 *   File : line-chart.js
 *   Author : https://evoqins.com
 *   Description : line chart component
 *   Integrations : NA
 *   Version : 1.0.0
 */
import React, { useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler,
  Plugin,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler,
);

// Custom plugin for vertical hover line (like the design)
const verticalLinePlugin = {
  id: "verticalLine",
  afterDraw(chart) {
    const tooltip = chart.tooltip;
    const chartArea = chart.chartArea;

    if (!chartArea || !tooltip || tooltip.opacity === 0) return;

    // Prefer the tooltip caret X/Y; fall back to the active element
    let x, y;
    if (tooltip.caretX !== undefined) {
      x = tooltip.caretX;
      y = tooltip.caretY;
    } else if (tooltip.dataPoints && tooltip.dataPoints.length > 0) {
      const activePoint = tooltip.dataPoints[0];
      if (!activePoint || !activePoint.element) return;
      x = activePoint.element.x;
      y = activePoint.element.y;
    } else {
      return;
    }

    const ctx = chart.ctx;

    ctx.save();
    ctx.strokeStyle = "#1877F2"; // same blue as line
    ctx.lineWidth = 1;
    ctx.setLineDash([]);

    // Draw ONLY the vertical line from point to bottom (no horizontal line)
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();

    ctx.restore();
  },
};

// Plugin to draw static "0" label before the first X tick (matches design)
const zeroLabelPlugin = {
  id: "zeroLabelPlugin",
  afterDraw(chart) {
    const scale = chart.scales?.x;
    const ctx = chart.ctx;
    if (!scale || !ctx) return;

    const { left } = chart.chartArea;
    const y = scale.bottom + 24; // place below axis similar to tick labels

    ctx.save();
    ctx.font = "12px Rubik, sans-serif";
    ctx.fillStyle = "#000000";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("0", left - 16, y);
    ctx.restore();
  },
};

// Helper to create / reuse custom tooltip element
const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector(".line-chart-tooltip");
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.className = "line-chart-tooltip";
    Object.assign(tooltipEl.style, {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      pointerEvents: "none",
      padding: "8px 16px",
      minWidth: "66px",
      minHeight: "44px",
      borderRadius: "80px",
      background:
        "linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.4) 100%), linear-gradient(0deg, #1877F2, #1877F2), #1877F2",
      backgroundBlendMode: "normal, overlay, multiply",
      color: "#fff",
      fontFamily: "Rubik, sans-serif",
      fontSize: "14px",
      fontWeight: "500",
      lineHeight: "18px",
      boxShadow: "0 6px 18px rgba(24, 119, 242, 0.35)",
      transition: "opacity 120ms ease",
      opacity: 0,
      transform: "translate(-50%, -100%) translateY(-12px)",
      whiteSpace: "nowrap",
      zIndex: 1,
    });

    const parent = chart.canvas.parentNode;
    if (getComputedStyle(parent).position === "static") {
      parent.style.position = "relative";
    }
    parent.appendChild(tooltipEl);
  }

  return tooltipEl;
};

// Custom tooltip handler to mimic Figma pill
const pillTooltipHandler = (context) => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  if (!tooltip || tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  const dataPoint = tooltip.dataPoints?.[0];
  if (!dataPoint) return;

  const rawValue = Number(dataPoint.raw) || 0;
  const formatted =
    rawValue >= 1000 ? `${Math.round(rawValue / 1000)}K` : `${rawValue}`;

  tooltipEl.innerText = formatted;

  const { offsetLeft, offsetTop } = chart.canvas;
  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = offsetLeft + tooltip.caretX + "px";
  tooltipEl.style.top = offsetTop + tooltip.caretY + "px";
};

export default function LineChart({ labels = [], datasets = [] }) {
  const chartRef = useRef(null);

  const allValues = datasets.flatMap((d) => d.data);
  const dataMax = Math.max(...allValues);

  // Fallback when all values are 0
  let max = 10;
  let stepSize = 2;

  if (dataMax > 0) {
    const targetSteps = 8;
    const rawStep = dataMax / targetSteps;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));

    const niceMultipliers = [1, 2, 2.5, 5, 10];
    const normalised = rawStep / magnitude;
    const niceMult = niceMultipliers.find((m) => m >= normalised) ?? 10;

    stepSize = niceMult * magnitude;
    max = Math.ceil(dataMax / stepSize) * stepSize;
  }
  const data = {
    labels,
    datasets: datasets.map((d) => ({
      ...d,
      tension: 0,
      fill: true,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointStyle: "circle",
      pointBackgroundColor: "transparent",
      pointBorderColor: "transparent",
      pointHoverBackgroundColor: "#ffffff",
      pointHoverBorderColor: "#1877F2",
      pointHoverBorderWidth: 1,

      backgroundColor: function (context) {
        const chart = context.chart;
        const { ctx, chartArea } = chart;

        if (!chartArea) return null; // needed during initial load

        const gradient = ctx.createLinearGradient(
          0,
          chartArea.top,
          0,
          chartArea.bottom,
        );

        // Your gradient stops
        gradient.addColorStop(0, "rgba(24, 119, 242, 0.1)");
        gradient.addColorStop(1, "rgba(255 ,255 ,255, 0.0)");

        return gradient;
      },
    })),
  };

  const options = {
    responsive: true,

    plugins: {
      tooltip: {
        enabled: false, // we'll draw a custom tooltip
        external: pillTooltipHandler,
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        offset: false,
        grid: {
          display: false, // remove grid lines
          drawBorder: false, // remove bottom axis line
        },
        border: {
          display: false, // remove axis border completely
        },
        ticks: {
          font: { family: "Rubik" },
          color: "black",
          display: true, // keep labels OR set to false to remove
          padding: 8,
        },
      },

      y: {
        min: 0,
        max: max, // ₹110K max as per Figma
        grid: {
          display: false, // remove horizontal grid lines
          drawBorder: false, // remove left axis line
        },
        border: {
          display: false, // remove axis border completely
        },
        ticks: {
          font: { family: "Rubik" },
          color: "black",
          display: true,
          stepSize: stepSize, // ✅ dynamic — was hardcoded at 10000 before
          padding: 8,
          callback: function (value) {
            if (value === 0) return "0";
            if (value >= 1000) return "₹" + value / 1000 + "K";
            return value;
          },
        },
        beginAtZero: true,
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
  };

  return (
    <Line
      ref={chartRef}
      data={data}
      options={options}
      plugins={[verticalLinePlugin, zeroLabelPlugin]}
      className="e-cursor-pointer"
    />
  );
}
